import Box from 'components/shared/Box'
import Modal from 'components/shared/Modal'
import Gallery from 'components/shared/Gallery'
import Text from 'components/shared/Text'
import SinglePhoto from 'components/shared/Gallery/SinglePhoto'
import villagePhoto1 from 'assets/images/accommodation/modal/suntagoVillageOverview.jpg'
import villagePhoto2 from 'assets/images/accommodation/modal/suntagoVillagePlaytime.jpg'
import villagePhoto3 from 'assets/images/accommodation/modal/suntagoVillageDining.jpg'
import villagePhoto1Mobile from 'assets/images/accommodation/modal/suntagoVillageOverview-mobile.jpg'
import villagePhoto2Mobile from 'assets/images/accommodation/modal/suntagoVillagePlaytime-mobile.jpg'
import villagePhoto3Mobile from 'assets/images/accommodation/modal/suntagoVillageDining-mobile.jpg'
import IconWithText from 'components/shared/IconWithText'
import { useResolutions } from 'store/resolutions'
import { BungalowInfoBanner } from './components/BungalowInfoBanner/BungalowInfoBanner'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    AccommodationModalBody,
    AccommodationModalHeader,
} from './AccommodationModal.styles'
import Grid from 'components/shared/Grid'
import { AccommodationModalProps } from './AccommodationModal.types'
import { bungalowsData, amenitiesData } from './utils/data'

export const AccommodationModal = ({
    isOpened,
    closeModal,
}: AccommodationModalProps) => {
    const { formatMessage } = useIntl()
    const { isMobile } = useResolutions()

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={770}
            onClose={closeModal}
            isMobileFullscreen={true}
            testIdPrefix="accommo-about"
        >
            <>
                <AccommodationModalHeader
                    pt={[16, 32]}
                    pb={[16, 32]}
                    pl={[16, 40]}
                >
                    <Text variant="heading3">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.title"
                            defaultMessage="_"
                        />
                    </Text>
                </AccommodationModalHeader>
                <AccommodationModalBody px={[16, 40]} py={[24, 40]}>
                    {/* teaser */}
                    <Text variant="body" color="grayscale_900" pb={24}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.teaser"
                            defaultMessage="_"
                        />
                    </Text>

                    {/* gallery with overview of the area */}
                    <Gallery hasAlternativeLayout={true}>
                        <SinglePhoto
                            photoPath={
                                isMobile ? villagePhoto1Mobile : villagePhoto1
                            }
                            alt="Suntago Village - aerial view of the area"
                        />
                        <SinglePhoto
                            photoPath={
                                isMobile ? villagePhoto2Mobile : villagePhoto2
                            }
                            alt="Suntago Village - family playing outdoor games"
                        />
                        <SinglePhoto
                            photoPath={
                                isMobile ? villagePhoto3Mobile : villagePhoto3
                            }
                            alt="Syntago Village - family dinig outside"
                        />
                    </Gallery>

                    {/* list of amenities */}
                    <Text variant="heading4" pt={48}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.amenities_section_caption"
                            defaultMessage="_"
                        />
                    </Text>
                    <Grid mt={24} columns={[1, 2]} gap={24} hasEvenColumns>
                        {amenitiesData.map(
                            ({
                                highlightedText,
                                additionalText,
                                icon,
                                iconAlt,
                            }) => (
                                <IconWithText
                                    align="center"
                                    key={highlightedText.id}
                                    icon={icon}
                                    iconAlt={iconAlt}
                                    textHighlighted={formatMessage(
                                        highlightedText
                                    )}
                                    text={
                                        additionalText
                                            ? formatMessage(additionalText)
                                            : null
                                    }
                                    textColor="grayscale_800"
                                    fontVariant="body14"
                                    hasFullLineTexts
                                />
                            )
                        )}
                    </Grid>

                    {/* detailed info about bungalows */}
                    <Text variant="heading4" pt={48}>
                        <FormattedMessage
                            id="accommodation.bungalow_modal.bungalows_section_caption"
                            defaultMessage="_"
                        />
                    </Text>
                    <Box mt={24} gap={[16, 24]} isFullWidth>
                        {bungalowsData.map(
                            ({
                                id,
                                name,
                                description,
                                maxGuests,
                                image,
                                equipment,
                                equipmentPremium,
                                isNew,
                                isPremium,
                            }) => (
                                <BungalowInfoBanner
                                    id={id}
                                    name={formatMessage(name)}
                                    description={formatMessage(description)}
                                    maxGuests={maxGuests}
                                    image={image}
                                    equipment={equipment}
                                    equipmentPremium={equipmentPremium}
                                    isNew={isNew}
                                    isPremium={isPremium}
                                    key={name.id}
                                />
                            )
                        )}
                    </Box>

                    {/* price disclaimer */}
                    <Text variant="bodySmall" pt={16} color="grayscale_800">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.disclaimer_price_dependence"
                            defaultMessage="_"
                        />
                    </Text>
                </AccommodationModalBody>
            </>
        </Modal>
    )
}

AccommodationModal.displayName = 'AccommodationModal'
